/** Actor-owned shell choices. Typography and custom files stay on the device. */
export const LIGHT_STUDIES = ["none", "downlight", "pool", "rim", "wash", "ambient", "combined"] as const;
export const LIGHT_INTENSITIES = ["whisper", "quiet", "present"] as const;
export const DEFAULT_ACTOR_APPEARANCE = Object.freeze({
  density: "comfortable" as "comfortable" | "compact",
  ambientMotion: true,
  toneDownSections: false,
  shellGlass: "frosted" as "frosted" | "opaque",
  glassOpacity: 80,
  lightStudy: "combined" as typeof LIGHT_STUDIES[number],
  lightIntensity: "quiet" as typeof LIGHT_INTENSITIES[number],
  lightBreathe: true,
  hoverLight: true,
  opaqueDoors: true,
  doorFade: true,
  cardGlow: 100,
});
export type ActorAppearancePreferences = {
  readonly [K in keyof typeof DEFAULT_ACTOR_APPEARANCE]: typeof DEFAULT_ACTOR_APPEARANCE[K] extends boolean ? boolean
    : typeof DEFAULT_ACTOR_APPEARANCE[K] extends number ? number : typeof DEFAULT_ACTOR_APPEARANCE[K];
};
export const ACTOR_APPEARANCE_KEYS = Object.keys(DEFAULT_ACTOR_APPEARANCE) as Array<keyof ActorAppearancePreferences>;
const bool = (v: unknown, fallback: boolean) => typeof v === "boolean" ? v : fallback;
const percent = (v: unknown, fallback: number) => typeof v === "number" && Number.isFinite(v) ? Math.max(0, Math.min(100, Math.round(v))) : fallback;
const choice = <T extends string>(v: unknown, choices: readonly T[], fallback: T): T => choices.includes(v as T) ? v as T : fallback;
/** One total parser for wire reads, local mirrors, and server writes. Unknown keys never roam. */
export function parseActorAppearance(value: unknown): ActorAppearancePreferences {
  const v = value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
  const d = DEFAULT_ACTOR_APPEARANCE;
  return {
    density: choice(v.density, ["comfortable", "compact"], d.density),
    ambientMotion: bool(v.ambientMotion, d.ambientMotion),
    toneDownSections: bool(v.toneDownSections, d.toneDownSections),
    shellGlass: choice(v.shellGlass, ["frosted", "opaque"], d.shellGlass),
    glassOpacity: Math.max(40, percent(v.glassOpacity, d.glassOpacity)),
    lightStudy: choice(v.lightStudy, LIGHT_STUDIES, d.lightStudy),
    lightIntensity: choice(v.lightIntensity, LIGHT_INTENSITIES, d.lightIntensity),
    lightBreathe: bool(v.lightBreathe, d.lightBreathe),
    hoverLight: bool(v.hoverLight, d.hoverLight),
    opaqueDoors: bool(v.opaqueDoors, d.opaqueDoors),
    doorFade: bool(v.doorFade, d.doorFade),
    cardGlow: percent(v.cardGlow, d.cardGlow),
  };
}
export function lightIntensityFactor(value: ActorAppearancePreferences["lightIntensity"]): number {
  return value === "whisper" ? 0.55 : value === "present" ? 1.5 : 1;
}
